import { useState } from "react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Calculator, Package, Truck, Wrench, Zap, ArrowRight, CheckCircle, Euro, Info, TrendingUp, Settings } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { ConfiguratorProvider, useConfigurator } from "@/components/configurator/ConfiguratorProvider";
import { ConfiguratorModal } from "@/components/configurator/ConfiguratorModal";
import { SEO } from "@/components/seo/SEO";

const ZonnepaneelPakkettenContent = () => {
  const { setIsOpen } = useConfigurator();
  
  // Input states
  const [jaarverbruik, setJaarverbruik] = useState(6500); // kWh per jaar (realistisch voor €300/maand)
  const [gasverbruik, setGasverbruik] = useState(1200); // m³ per jaar (standaard Nederlands huishouden)
  const [postcode, setPostcode] = useState("");
  const [dakOppervlak, setDakOppervlak] = useState([40]);
  const [dakOrientatie, setDakOrientatie] = useState("zuid");
  const [dakHelling, setDakHelling] = useState([35]);
  const [energieTarief, setEnergieTarief] = useState(0.07); // €/kWh EasyStroom dynamisch
  const [contractType, setContractType] = useState("dynamisch");
  const [gasTarief, setGasTarief] = useState(0.34); // €/m³ EasyStroom dynamisch
  const [huidigeEnergieTarief, setHuidigeEnergieTarief] = useState(0.65); // €/kWh oude leverancier (€300/maand niveau)
  const [huidigeGasTarief, setHuidigeGasTarief] = useState(1.45); // €/m³ oude leverancier (realistisch)
  
  // Sol-Vi ecosysteem percentages (totaal moet 100% zijn)
  const [afbetalingPercentage, setAfbetalingPercentage] = useState(50);
  const [upgradesPercentage, setUpgradesPercentage] = useState(30);
  const [laadparkPercentage, setLaadparkPercentage] = useState(10);
  const [goedeDoelenPercentage, setGoedeDoelenPercentage] = useState(10);

  // Berekeningsconstanten
  const panelVermogen = 0.4; // kWp per paneel (400W)
  const panelOppervlak = 2.0; // m² per paneel
  const basisZonnestraling = 1000; // kWh/kWp/jaar voor Nederland
  const systeemVerliezen = 0.15; // 15% systeem verliezen
  const eigenverbruikPercentage = 0.70; // 70% direct verbruik
  const terugleverTarief = 0.04; // €/kWh teruglevering EasyStroom dynamisch
  const energiePrijsStijging = 0.03; // 3% per jaar
  
  // Oriëntatie correctiefactoren
  const orientatieFactoren = {
    zuid: 1.0,
    zuidoost: 0.93,
    zuidwest: 0.93,
    oost: 0.78,
    west: 0.78,
    noord: 0.6
  };

  // Helling correctiefactoren (optimaal = 35°)
  const getHellingFactor = (helling: number) => {
    if (helling < 0 || helling > 60) return 0.7;
    if (helling >= 30 && helling <= 40) return 1.0;
    if (helling >= 20 && helling <= 50) return 0.95;
    if (helling >= 10 && helling <= 60) return 0.85;
    return 0.75;
  };

  // Geavanceerde berekeningen
  const maxPanelen = Math.floor(dakOppervlak[0] / panelOppervlak);
  const orientatieFactor = orientatieFactoren[dakOrientatie as keyof typeof orientatieFactoren] || 0.8;
  const hellingFactor = getHellingFactor(dakHelling[0]);
  
  // Opbrengst per paneel per jaar
  const opbrengstPerPaneel = panelVermogen * basisZonnestraling * orientatieFactor * hellingFactor * (1 - systeemVerliezen);
  
  // Benodigde panelen op basis van jaarverbruik
  const benodigd = Math.min(Math.ceil(jaarverbruik / opbrengstPerPaneel), maxPanelen);
  const totaalVermogen = benodigd * panelVermogen;
  const jaarlijkseOpbrengst = benodigd * opbrengstPerPaneel; // kWh per jaar
  
  // Sol-Vi Business Model Berekeningen met Dubbele Besparing
  const eigenverbruik = Math.min(jaarverbruik, jaarlijkseOpbrengst * eigenverbruikPercentage);
  const teruglevering = Math.max(0, jaarlijkseOpbrengst - eigenverbruik);
  
  // Besparing door zonnepanelen (EasyStroom tarieven)
  const besparingEigenverbruik = eigenverbruik * energieTarief;
  const opbrengstTeruglevering = teruglevering * terugleverTarief;
  const zonnepaneelBesparing = besparingEigenverbruik + opbrengstTeruglevering;
  
  // Extra besparing door overstap naar EasyStroom (voor niet-zonne energie)
  const restEnergieverbruik = jaarverbruik - eigenverbruik;
  const energiecontractBesparing = restEnergieverbruik * (huidigeEnergieTarief - energieTarief);
  
  // Gas besparing door overstap naar EasyStroom
  const gasBesparing = gasverbruik * (huidigeGasTarief - gasTarief);
  
  // Totale jaarlijkse besparing (DRIEVOUDIG: zonnepanelen + goedkoper stroom + goedkoper gas)
  const jaarlijkseBesparing = zonnepaneelBesparing + energiecontractBesparing + gasBesparing;
  
  const maandelijkseBesparing = Math.round(jaarlijkseBesparing / 12);
  const systeemWaarde = benodigd * 400; // €400 per paneel totale systeemwaarde
  const alBetaaldViaPakket = benodigd * 200; // €200 per paneel al betaald via pakket
  const restSchuldSolVi = systeemWaarde - alBetaaldViaPakket; // €200 per paneel nog af te betalen
  const totaalInvestering = benodigd * 190; // Inkoopprijs materialen en installatie
  
  // Sol-Vi ecosystem betalingen gebaseerd op percentages
  const solViMaandBetaling = Math.round((maandelijkseBesparing * afbetalingPercentage) / 100);
  const upgradesMaand = Math.round((maandelijkseBesparing * upgradesPercentage) / 100);
  const laadparkMaand = Math.round((maandelijkseBesparing * laadparkPercentage) / 100);
  const goedeDoelenMaand = Math.round((maandelijkseBesparing * goedeDoelenPercentage) / 100);
  const directVoordeelMaand = maandelijkseBesparing - solViMaandBetaling - upgradesMaand - laadparkMaand - goedeDoelenMaand;
  const afbetaalMaanden = restSchuldSolVi > 0 && solViMaandBetaling > 0 ? Math.ceil(restSchuldSolVi / solViMaandBetaling) : 0;
  
  // Impact berekeningen
  const jaarLaadparkBijdrage = laadparkMaand * 12;
  const jaarGoedeDoelenBijdrage = goedeDoelenMaand * 12;
  const gezinnenGeholpen = Math.floor(jaarGoedeDoelenBijdrage / 600); // €600 per gezin per jaar
  const laadparkROI = jaarLaadparkBijdrage * 0.08; // 8% jaarlijks rendement verwacht
  
  // Na afbetaling berekeningen (25 jaar perspectief)
  const afbetaalJaren = Math.ceil(afbetaalMaanden / 12);
  const jarenNaAfbetaling = Math.max(0, 25 - afbetaalJaren);
  
  // Totale besparing gedurende afbetaalperiode gaat naar Sol-Vi
  const besparingTijdensAfbetaling = Array.from({length: afbetaalJaren}, (_, year) => {
    const jaarFactor = Math.pow(1 + energiePrijsStijging, year);
    return jaarlijkseBesparing * jaarFactor;
  }).reduce((sum, yearly) => sum + yearly, 0);
  
  // Na afbetaling gaat alle besparing naar de klant
  const besparingNaAfbetaling = Array.from({length: jarenNaAfbetaling}, (_, year) => {
    const jaarFactor = Math.pow(1 + energiePrijsStijging, afbetaalJaren + year);
    return jaarlijkseBesparing * jaarFactor;
  }).reduce((sum, yearly) => sum + yearly, 0);
  
  const totaleKlantVoordeel = besparingNaAfbetaling; // Wat klant overhoudt na afbetaling

  // Bereken besparing per pakket met nieuwe tarieven (drievoudige besparing)
  const berekenPakketBesparing = (aantalPanelen: number) => {
    const pakketOpbrengst = aantalPanelen * opbrengstPerPaneel;
    const pakketEigenverbruik = Math.min(jaarverbruik, pakketOpbrengst * eigenverbruikPercentage);
    const pakketTeruglevering = Math.max(0, pakketOpbrengst - pakketEigenverbruik);
    
    const pakketZonneBesparing = (pakketEigenverbruik * energieTarief) + (pakketTeruglevering * terugleverTarief);
    const pakketRestVerbruik = jaarverbruik - pakketEigenverbruik;
    const pakketContractBesparing = pakketRestVerbruik * (huidigeEnergieTarief - energieTarief);
    const pakketGasBesparing = gasverbruik * (huidigeGasTarief - gasTarief);
    
    const pakketMaandBesparing = Math.round((pakketZonneBesparing + pakketContractBesparing + pakketGasBesparing) / 12);
    
    // Sol-Vi ecosystem betalingen
    const pakketSolViBetaling = Math.round((pakketMaandBesparing * afbetalingPercentage) / 100);
    const pakketUpgrades = Math.round((pakketMaandBesparing * upgradesPercentage) / 100);
    const pakketLaadpark = Math.round((pakketMaandBesparing * laadparkPercentage) / 100);
    const pakketGoedeDoelenBijdrage = Math.round((pakketMaandBesparing * goedeDoelenPercentage) / 100);
    const pakketDirectVoordeel = pakketMaandBesparing - pakketSolViBetaling - pakketUpgrades - pakketLaadpark - pakketGoedeDoelenBijdrage;
    
    return {
      totaalBesparing: pakketMaandBesparing,
      solViBetaling: pakketSolViBetaling,
      directVoordeel: pakketDirectVoordeel
    };
  };

  const pakketten = [
    {
      naam: "Starter Systeem",
      panelen: 8,
      vermogen: 3.2,
      originalPrijs: 1600, // 8 × €200 per paneel
      prijs: Math.round(1600 * 0.9), // 10% korting
      geschikt: "Appartementen en kleine woningen",
      ...berekenPakketBesparing(8)
    },
    {
      naam: "Comfort Systeem", 
      panelen: 12,
      vermogen: 4.8,
      originalPrijs: 2400, // 12 × €200 per paneel
      prijs: Math.round(2400 * 0.9), // 10% korting
      geschikt: "Tussenwoning en eengezinswoning",
      ...berekenPakketBesparing(12)
    },
    {
      naam: "Premium Systeem",
      panelen: 16, 
      vermogen: 6.4,
      originalPrijs: 3200, // 16 × €200 per paneel
      prijs: Math.round(3200 * 0.9), // 10% korting
      geschikt: "Vrijstaande woning en villa",
      ...berekenPakketBesparing(16)
    },
    {
      naam: "Maximaal Systeem",
      panelen: 20,
      vermogen: 8.0,
      originalPrijs: 4000, // 20 × €200 per paneel
      prijs: Math.round(4000 * 0.9), // 10% korting
      geschikt: "Grote villa's en zakelijke toepassingen",
      ...berekenPakketBesparing(20)
    }
  ];

  const procesStappen = [
    {
      icon: Calculator,
      titel: "Gratis Advies & Berekening",
      beschrijving: "Professionele analyse van uw energiebehoefte en optimale systeemconfiguratie"
    },
    {
      icon: Package,
      titel: "Technische Inspectie", 
      beschrijving: "Grondige evaluatie van uw dak en elektrische installatie door gecertificeerde specialisten"
    },
    {
      icon: Truck,
      titel: "Logistiek & Levering",
      beschrijving: "Gecoördineerde levering van alle hoogwaardige systeemcomponenten op afgesproken datum"
    },
    {
      icon: Wrench,
      titel: "Gecertificeerde Installatie",
      beschrijving: "Volledige systeeminstallatie door erkende vakspecialisten met 10 jaar garantie"
    },
    {
      icon: Zap,
      titel: "Inbedrijfstelling & Monitoring",
      beschrijving: "Professionele netaansluiting en 24/7 remote monitoring voor optimale prestaties"
    }
  ];

  const handleBestelling = (pakket: typeof pakketten[0]) => {
    toast({
      title: "Offerteverzoek verzonden",
      description: `${pakket.naam} geselecteerd. Onze specialisten nemen binnen 24 uur contact op voor een vrijblijvende bespreking.`,
    });
  };

  return (
      <div className="min-h-screen bg-background">
        <Header />
        <ConfiguratorModal />
      <main>
        <SEO
          title="Zonnepaneel pakketten en besparing | SOL-VI"
          description="Bereken uw investering en maandelijkse besparing. Complete pakketten met dynamische energietarieven voor maximaal rendement."
          type="website"
        />
        {/* Hero Section */}
        <section className="py-20 px-4 bg-gradient-to-br from-primary/10 via-background to-secondary/10">
          <div className="container mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Minimale investering
              <span className="text-primary"> voor maximaal rendement</span>
            </h1>
            <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
              Sol-Vi biedt complete zonnepaneel installaties gecombineerd met voordelige energietarieven van EasyStroom. 
              Onze geïntegreerde aanpak zorgt voor optimale besparingen door efficiënte zonneopbrengst, 
              concurrerende stroomtarieven (€0,07/kWh) en gasleverantie (€0,34/m³).
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button size="lg" className="bg-primary hover:bg-primary/90">
                Vraag Offerte Aan <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                onClick={() => setIsOpen(true)}
                className="border-primary text-primary hover:bg-primary/10"
              >
                <Settings className="mr-2 h-4 w-4" />
                Configureer Systeem
              </Button>
              <Button size="lg" variant="outline">
                Onze Voordelen
              </Button>
            </div>
          </div>
        </section>

        {/* Calculator Section */}
        <section className="py-20 px-4 bg-muted/50">
          <div className="container mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Bereken Uw Investering & Besparingen</h2>
              <p className="text-xl text-muted-foreground mb-6">
                Krijg inzicht in uw maandelijkse besparingen en terugverdientijd met onze geavanceerde calculator
              </p>
              <div className="max-w-4xl mx-auto p-6 bg-primary/5 rounded-lg border border-primary/20">
                <p className="text-muted-foreground leading-relaxed">
                  <strong>Onze Filosofie:</strong> Bij Sol-Vi geloven wij in transparante en duurzame financiële modellen. 
                  Onze installaties zijn ontworpen om zichzelf terug te verdienen door energiebesparingen. 
                  Na de initiële terugverdienperiode kunt u kiezen om uw besparingen te blijven reserveren voor toekomstige investeringen 
                  zoals batterijopslag, laadpaal installatie of uitbreiding van uw systeem.
                </p>
              </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-12 items-start">
              {/* Input Section */}
              <Card className="p-6">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calculator className="h-5 w-5" />
                    Uw Energieprofiel
                  </CardTitle>
                  <CardDescription>
                    Voer uw huidige energiegegevens in voor een accurate berekening van potentiële besparingen
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <Label htmlFor="jaarverbruik" className="text-base font-medium flex items-center gap-2">
                      Jaarverbruik stroom (kWh)
                      <Info className="h-4 w-4 text-muted-foreground" />
                    </Label>
                    <div className="mt-2">
                      <Slider
                        value={[jaarverbruik]}
                        onValueChange={(value) => setJaarverbruik(value[0])}
                        max={12000}
                        min={2000}
                        step={250}
                        className="w-full"
                      />
                      <div className="flex justify-between text-sm text-muted-foreground mt-2">
                        <span>2.000 kWh</span>
                        <span className="font-medium text-primary">{jaarverbruik.toLocaleString()} kWh</span>
                        <span>12.000+ kWh</span>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Te vinden op jouw laatste jaarafrekening (€300/maand = ca. 6.500 kWh)
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="gasverbruik" className="text-base font-medium flex items-center gap-2">
                      Jaarverbruik gas (m³)
                      <Info className="h-4 w-4 text-muted-foreground" />
                    </Label>
                    <div className="mt-2">
                      <Slider
                        value={[gasverbruik]}
                        onValueChange={(value) => setGasverbruik(value[0])}
                        max={3000}
                        min={500}
                        step={100}
                        className="w-full"
                      />
                      <div className="flex justify-between text-sm text-muted-foreground mt-2">
                        <span>500 m³</span>
                        <span className="font-medium text-primary">{gasverbruik.toLocaleString()} m³</span>
                        <span>3.000+ m³</span>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Gemiddeld Nederlands huishouden: 1.200 m³/jaar
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="postcode" className="text-base font-medium">Postcode</Label>
                    <Input
                      id="postcode"
                      value={postcode}
                      onChange={(e) => setPostcode(e.target.value)}
                      placeholder="1234 AB"
                      className="mt-2"
                    />
                  </div>

                  <div>
                    <Label className="text-base font-medium">Dakoriëntatie</Label>
                    <Select value={dakOrientatie} onValueChange={setDakOrientatie}>
                      <SelectTrigger className="mt-2">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="zuid">Zuid (optimaal)</SelectItem>
                        <SelectItem value="zuidoost">Zuidoost</SelectItem>
                        <SelectItem value="zuidwest">Zuidwest</SelectItem>
                        <SelectItem value="oost">Oost</SelectItem>
                        <SelectItem value="west">West</SelectItem>
                        <SelectItem value="noord">Noord</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="text-base font-medium">Dakhellinghoek (°)</Label>
                    <div className="mt-2">
                      <Slider
                        value={dakHelling}
                        onValueChange={setDakHelling}
                        max={60}
                        min={0}
                        step={5}
                        className="w-full"
                      />
                      <div className="flex justify-between text-sm text-muted-foreground mt-2">
                        <span>0° (plat)</span>
                        <span className="font-medium text-primary">{dakHelling[0]}°</span>
                        <span>60°</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <Label className="text-base font-medium">Beschikbaar dakoppervlak (m²)</Label>
                    <div className="mt-2">
                      <Slider
                        value={dakOppervlak}
                        onValueChange={setDakOppervlak}
                        max={100}
                        min={20}
                        step={5}
                        className="w-full"
                      />
                      <div className="flex justify-between text-sm text-muted-foreground mt-2">
                        <span>20m²</span>
                        <span className="font-medium text-primary">{dakOppervlak[0]}m²</span>
                        <span>100m²+</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="huidigeTarief" className="text-base font-medium">Huidige energietarief (€/kWh)</Label>
                    <div className="mt-2">
                      <Slider
                        value={[huidigeEnergieTarief * 100]}
                        onValueChange={(value) => setHuidigeEnergieTarief(value[0] / 100)}
                        max={85}
                        min={45}
                        step={1}
                        className="w-full"
                      />
                      <div className="flex justify-between text-sm text-muted-foreground mt-2">
                        <span>€0.45</span>
                        <span className="font-medium text-primary">€{huidigeEnergieTarief.toFixed(2)}/kWh</span>
                        <span>€0.85+</span>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      €300/maand = ca. €0.65/kWh bij grote leveranciers
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="huidigeGasTarief" className="text-base font-medium">Huidige gastarief (€/m³)</Label>
                    <div className="mt-2">
                      <Slider
                        value={[huidigeGasTarief * 100]}
                        onValueChange={(value) => setHuidigeGasTarief(value[0] / 100)}
                        max={200}
                        min={100}
                        step={5}
                        className="w-full"
                      />
                      <div className="flex justify-between text-sm text-muted-foreground mt-2">
                        <span>€1.00</span>
                        <span className="font-medium text-primary">€{huidigeGasTarief.toFixed(2)}/m³</span>
                        <span>€2.00+</span>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Gemiddeld gastarief grote leveranciers: €1.45/m³
                    </p>
                  </div>

                  <div>
                    <Label className="text-base font-medium">EasyStroom contract keuze</Label>
                    <Select value={contractType} onValueChange={setContractType}>
                      <SelectTrigger className="mt-2">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="dynamisch">Dynamisch (€0.07/kWh + €7/maand)</SelectItem>
                        <SelectItem value="smart">SMART Dynamisch (€0.07/kWh + €10/maand + handelmogelijkheid)</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground mt-1">
                      Sol-Vi regelt je overstap naar EasyStroom
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="gastarief" className="text-base font-medium">EasyStroom gastarief (€/m³)</Label>
                    <Input
                      id="gastarief"
                      type="number"
                      step="0.01"
                      value={gasTarief}
                      onChange={(e) => setGasTarief(parseFloat(e.target.value) || 0)}
                      className="mt-2"
                      disabled
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Dynamisch gastarief inclusief in EasyStroom contract
                    </p>
                  </div>

                   <div>
                     <Label className="text-base font-medium">Sol-Vi Ecosystem Verdeling</Label>
                     <div className="grid grid-cols-2 gap-4 mt-4">
                       <div>
                         <Label className="text-sm text-muted-foreground">Afbetaling Systeem</Label>
                         <Slider
                           value={[afbetalingPercentage]}
                           onValueChange={(value) => {
                             const remaining = 100 - value[0];
                             const ratio = remaining / (upgradesPercentage + laadparkPercentage + goedeDoelenPercentage);
                             setAfbetalingPercentage(value[0]);
                             setUpgradesPercentage(Math.round(upgradesPercentage * ratio));
                             setLaadparkPercentage(Math.round(laadparkPercentage * ratio));
                             setGoedeDoelenPercentage(100 - value[0] - Math.round(upgradesPercentage * ratio) - Math.round(laadparkPercentage * ratio));
                           }}
                           max={80}
                           min={30}
                           step={5}
                           className="w-full mt-1"
                         />
                         <div className="text-xs text-primary mt-1">{afbetalingPercentage}%</div>
                       </div>
                       
                       <div>
                         <Label className="text-sm text-muted-foreground">Toekomstige Upgrades</Label>
                         <Slider
                           value={[upgradesPercentage]}
                           onValueChange={(value) => {
                             const newTotal = afbetalingPercentage + value[0] + laadparkPercentage + goedeDoelenPercentage;
                             if (newTotal <= 100) {
                               setUpgradesPercentage(value[0]);
                             }
                           }}
                           max={50}
                           min={0}
                           step={5}
                           className="w-full mt-1"
                         />
                         <div className="text-xs text-primary mt-1">{upgradesPercentage}%</div>
                       </div>
                       
                       <div>
                         <Label className="text-sm text-muted-foreground">Laadpark Investering</Label>
                         <Slider
                           value={[laadparkPercentage]}
                           onValueChange={(value) => {
                             const newTotal = afbetalingPercentage + upgradesPercentage + value[0] + goedeDoelenPercentage;
                             if (newTotal <= 100) {
                               setLaadparkPercentage(value[0]);
                             }
                           }}
                           max={30}
                           min={0}
                           step={5}
                           className="w-full mt-1"
                         />
                         <div className="text-xs text-primary mt-1">{laadparkPercentage}%</div>
                       </div>
                       
                       <div>
                         <Label className="text-sm text-muted-foreground">Goede Doelen</Label>
                         <Slider
                           value={[goedeDoelenPercentage]}
                           onValueChange={(value) => {
                             const newTotal = afbetalingPercentage + upgradesPercentage + laadparkPercentage + value[0];
                             if (newTotal <= 100) {
                               setGoedeDoelenPercentage(value[0]);
                             }
                           }}
                           max={25}
                           min={0}
                           step={5}
                           className="w-full mt-1"
                         />
                         <div className="text-xs text-primary mt-1">{goedeDoelenPercentage}%</div>
                       </div>
                     </div>
                     <div className="text-xs text-muted-foreground mt-2 p-2 bg-muted/50 rounded">
                       <p>Totaal: {afbetalingPercentage + upgradesPercentage + laadparkPercentage + goedeDoelenPercentage}% | Direct voordeel: {100 - (afbetalingPercentage + upgradesPercentage + laadparkPercentage + goedeDoelenPercentage)}%</p>
                     </div>
                   </div>
                </CardContent>
              </Card>

              {/* Results Section */}
              <Card className="p-6 bg-primary/5 border-primary/20">
                <CardHeader>
                  <CardTitle className="text-2xl text-primary flex items-center gap-2">
                    <Euro className="h-6 w-6" />
                    Jouw Sol-Vi Partnership Overzicht
                  </CardTitle>
                  <CardDescription>
                    Gebaseerd op {jaarverbruik.toLocaleString()} kWh stroom + {gasverbruik.toLocaleString()} m³ gas per jaar
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center p-4 bg-background rounded-lg border">
                      <div className="text-2xl font-bold text-primary">{benodigd}</div>
                      <div className="text-sm text-muted-foreground">Panelen aanbevolen</div>
                    </div>
                    <div className="text-center p-4 bg-background rounded-lg border">
                      <div className="text-2xl font-bold text-green-600">€{solViMaandBetaling}</div>
                      <div className="text-sm text-muted-foreground">Sol-Vi betaling/maand</div>
                    </div>
                    <div className="text-center p-4 bg-background rounded-lg border">
                      <div className="text-2xl font-bold text-orange-600">€{Math.round(gasBesparing/12)}</div>
                      <div className="text-sm text-muted-foreground">Gas besparing/maand</div>
                    </div>
                  </div>

                  <div className="p-4 bg-green-50 dark:bg-green-950/30 rounded-lg border border-green-200 dark:border-green-800">
                    <h4 className="font-semibold text-green-800 dark:text-green-200 mb-2">💰 Drievoudige Besparing Breakdown</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span>🔋 Zonnepanelen besparing:</span>
                        <span className="font-medium">€{Math.round(zonnepaneelBesparing/12)}/maand</span>
                      </div>
                      <div className="flex justify-between">
                        <span>⚡ Stroom contract besparing:</span>
                        <span className="font-medium">€{Math.round(energiecontractBesparing/12)}/maand</span>
                      </div>
                      <div className="flex justify-between">
                        <span>🔥 Gas contract besparing:</span>
                        <span className="font-medium">€{Math.round(gasBesparing/12)}/maand</span>
                      </div>
                      <hr className="border-green-300 dark:border-green-700" />
                      <div className="flex justify-between font-bold text-base">
                        <span>💡 Totale maandelijkse besparing:</span>
                        <span className="text-green-600">€{maandelijkseBesparing}</span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span>Systeem vermogen:</span>
                      <span className="font-medium">{totaalVermogen.toFixed(1)} kWp</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Totale systeemwaarde:</span>
                      <span className="font-medium text-primary">€{systeemWaarde.toLocaleString()}</span>
                    </div>
                    
                    {/* Package Cost Breakdown */}
                    <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded border border-blue-200 dark:border-blue-800">
                      <div className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-2">💰 Sol-Vi Pakket Model:</div>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span>✅ Al betaald via pakket (€200/paneel):</span>
                          <span className="font-medium text-green-600">€{alBetaaldViaPakket.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>📋 Resterende systeemwaarde:</span>
                          <span className="font-medium text-orange-600">€{restSchuldSolVi.toLocaleString()}</span>
                        </div>
                         <div className="flex justify-between border-t border-blue-200 dark:border-blue-700 pt-1">
                           <span className="font-medium">💡 Totale maandelijkse besparing:</span>
                           <span className="font-bold text-green-600">€{maandelijkseBesparing}</span>
                         </div>
                         
                         <div className="space-y-1 mt-2">
                           <div className="flex justify-between">
                             <span>🔧 Sol-Vi Afbetaling ({afbetalingPercentage}%):</span>
                             <span className="font-medium text-primary">€{solViMaandBetaling}</span>
                           </div>
                           <div className="flex justify-between">
                             <span>🔋 Upgrades Potje ({upgradesPercentage}%):</span>
                             <span className="font-medium text-blue-600">€{upgradesMaand}</span>
                           </div>
                           <div className="flex justify-between">
                             <span>🚗 Laadpark Investering ({laadparkPercentage}%):</span>
                             <span className="font-medium text-purple-600">€{laadparkMaand}</span>
                           </div>
                           <div className="flex justify-between">
                             <span>❤️ Goede Doelen ({goedeDoelenPercentage}%):</span>
                             <span className="font-medium text-orange-600">€{goedeDoelenMaand}</span>
                           </div>
                           <div className="flex justify-between bg-green-100 dark:bg-green-900/30 px-2 py-1 rounded">
                             <span className="font-medium">💰 Direct in portemonnee:</span>
                             <span className="font-bold text-green-700 dark:text-green-300">€{directVoordeelMaand}</span>
                           </div>
                         </div>
                      </div>
                    </div>
                    
                    <div className="flex justify-between border-t pt-2">
                      <span className="font-medium">Afbetaaltermijn restschuld:</span>
                      <span className="font-bold text-primary">{afbetaalMaanden} maanden ({afbetaalJaren} jaar)</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Daarna is de volledige besparing voor jou:</span>
                      <span className="font-bold text-green-600">€{maandelijkseBesparing}/maand</span>
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20 p-4 rounded-lg border border-green-200 dark:border-green-800">
                    <div className="space-y-2">
                      <div className="text-sm font-medium text-green-800 dark:text-green-200">
                        Jouw 25-jaars voordeel overzicht:
                      </div>
                      <div className="grid grid-cols-1 gap-2 text-sm">
                        <div>
                          <span className="text-muted-foreground">Betaal Sol-Vi gedurende:</span>
                          <span className="ml-2 font-bold text-primary">{afbetaalJaren} jaar</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Daarna {jarenNaAfbetaling} jaar lang gratis energie:</span>
                          <span className="ml-2 font-bold text-green-600">€{Math.round(besparingNaAfbetaling).toLocaleString()}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Jouw totale voordeel na afbetaling:</span>
                          <span className="ml-2 font-bold text-green-600">€{Math.round(totaleKlantVoordeel).toLocaleString()}</span>
                        </div>
                      </div>
                      <div className="text-sm text-green-700 dark:text-green-200 pt-2 border-t border-green-200 dark:border-green-700">
                        <strong>Risico-vrij partnerschap:</strong> Betaal alleen wat je bespaart, daarna is alles voor jou!
                      </div>
                    </div>
                  </div>

                  {(orientatieFactor < 0.9 || hellingFactor < 0.9) && (
                    <div className="bg-amber-50 dark:bg-amber-900/20 p-4 rounded-lg border border-amber-200 dark:border-amber-800">
                      <div className="text-sm text-amber-800 dark:text-amber-200">
                        <strong>Let op:</strong> Jouw dak heeft een {orientatieFactor < 0.9 ? 'suboptimale oriëntatie' : 'suboptimale hellingshoek'}. 
                        De opbrengst kan {Math.round((1 - Math.min(orientatieFactor, hellingFactor)) * 100)}% lager zijn dan optimaal.
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
            
            {/* Impact Dashboard */}
            <div className="mt-16">
              <h3 className="text-2xl font-bold text-center mb-8">Jouw Impact in de SOL VI Community</h3>
              <div className="grid md:grid-cols-3 gap-6">
                <Card className="p-6 bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-950 dark:to-purple-900 border-purple-200 dark:border-purple-800">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg text-purple-800 dark:text-purple-200">Laadpark Investering</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-purple-600 dark:text-purple-400 mb-2">€{jaarLaadparkBijdrage}</div>
                    <p className="text-sm text-purple-700 dark:text-purple-300 mb-3">per jaar bijdrage</p>
                    <div className="text-sm text-muted-foreground">
                      <p>• Verwacht rendement: €{Math.round(laadparkROI)}/jaar</p>
                      <p>• Helpt EV-rijders goedkoop laden</p>
                      <p>• Groene energie uit ons netwerk</p>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="p-6 bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-950 dark:to-orange-900 border-orange-200 dark:border-orange-800">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg text-orange-800 dark:text-orange-200">Goede Doelen Impact</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-orange-600 dark:text-orange-400 mb-2">{gezinnenGeholpen}</div>
                    <p className="text-sm text-orange-700 dark:text-orange-300 mb-3">gezinnen per jaar geholpen</p>
                    <div className="text-sm text-muted-foreground">
                      <p>• €{jaarGoedeDoelenBijdrage} totale donatie</p>
                      <p>• Schoolreisjes & verjaardagen</p>
                      <p>• 100% transparant beheer</p>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="p-6 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900 border-blue-200 dark:border-blue-800">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg text-blue-800 dark:text-blue-200">Toekomst Projecties</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-blue-600 dark:text-blue-400 mb-2">€{Math.round(upgradesMaand * 12 * 5)}</div>
                    <p className="text-sm text-blue-700 dark:text-blue-300 mb-3">upgrade potje na 5 jaar</p>
                    <div className="text-sm text-muted-foreground">
                      <p>• Thuisbatterij binnen bereik</p>
                      <p>• Warmtepomp mogelijk</p>
                      <p>• EV-lader of zonneboiler</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Pakketten Section */}
        <section className="py-20 px-4">
          <div className="container mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Professionele Zonnepaneel Pakketten</h2>
              <p className="text-xl text-muted-foreground mb-6">
                Complete systemen inclusief hoogwaardige panelen, omvormer, montagesysteem, bekabeling en gecertificeerde installatie door vakspecialisten
              </p>
              <div className="flex justify-center gap-4">
                <Badge className="bg-green-100 text-green-800 text-lg px-4 py-2">
                  🏆 Beperkte tijd: 10% introductiekorting op alle installaties
                </Badge>
                <Button 
                  variant="outline" 
                  onClick={() => setIsOpen(true)}
                  className="border-primary text-primary hover:bg-primary/10"
                >
                  <Settings className="mr-2 h-4 w-4" />
                  Maatwerk Configuratie
                </Button>
              </div>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {pakketten.map((pakket, index) => (
                <Card key={index} className={`relative ${benodigd >= pakket.panelen - 2 && benodigd <= pakket.panelen + 2 ? 'ring-2 ring-primary bg-primary/5' : ''}`}>
                  {benodigd >= pakket.panelen - 2 && benodigd <= pakket.panelen + 2 && (
                     <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-primary text-primary-foreground px-3 py-1 rounded-full text-sm font-medium">
                       Optimaal voor uw situatie
                     </div>
                  )}
                  <CardHeader className="text-center">
                    <CardTitle className="text-xl">{pakket.naam}</CardTitle>
                    <CardDescription>{pakket.geschikt}</CardDescription>
                  </CardHeader>
                  <CardContent className="text-center space-y-4">
                    <div className="space-y-1">
                      <div className="text-sm text-muted-foreground line-through">€{pakket.originalPrijs.toLocaleString()}</div>
                      <div className="text-3xl font-bold text-primary">€{pakket.prijs.toLocaleString()}</div>
                       <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                         10% INTRODUCTIEKORTING
                       </Badge>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span>Panelen:</span>
                        <span className="font-medium">{pakket.panelen} stuks</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Vermogen:</span>
                        <span className="font-medium">{pakket.vermogen} kWp</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Totale besparing:</span>
                        <span className="font-medium text-green-600">€{pakket.totaalBesparing}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Sol-Vi betaling:</span>
                        <span className="font-medium text-blue-600">€{pakket.solViBetaling}</span>
                      </div>
                      <div className="flex justify-between bg-green-100 dark:bg-green-900/30 px-2 py-1 rounded">
                        <span className="text-sm">Direct voordeel:</span>
                        <span className="font-medium text-green-700 dark:text-green-300">€{pakket.directVoordeel}/maand</span>
                      </div>
                    </div>
                    <Button 
                      className="w-full" 
                      onClick={() => handleBestelling(pakket)}
                      variant={benodigd >= pakket.panelen - 2 && benodigd <= pakket.panelen + 2 ? "default" : "outline"}
                    >
                      Selecteer Pakket
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Proces Section */}
        <section className="py-20 px-4 bg-muted/50">
          <div className="container mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Zo Werkt Het</h2>
              <p className="text-xl text-muted-foreground">
                Van bestelling tot besparing in 5 eenvoudige stappen
              </p>
            </div>

            <div className="grid md:grid-cols-5 gap-8">
              {procesStappen.map((stap, index) => (
                <div key={index} className="text-center">
                  <div className="mx-auto w-16 h-16 bg-primary rounded-full flex items-center justify-center mb-4">
                    <stap.icon className="h-8 w-8 text-primary-foreground" />
                  </div>
                  <h3 className="font-semibold mb-2">{stap.titel}</h3>
                  <p className="text-sm text-muted-foreground">{stap.beschrijving}</p>
                  {index < procesStappen.length - 1 && (
                    <ArrowRight className="hidden md:block mx-auto mt-4 h-4 w-4 text-muted-foreground" />
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Voordelen Section */}
        <section className="py-20 px-4">
          <div className="container mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Sol-Vi Creëert <span className="text-primary">Ruimte in Je Portemonnee</span>
              </h2>
              <p className="text-xl text-muted-foreground">
                Waarom kiezen voor onze zonnepaneel pakketten?
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                {
                  icon: Euro,
                  titel: "Maandelijks Meer Over",
                  beschrijving: "Het systeem bespaart direct geld op je energierekening. Na terugverdientijd heb je maandelijks meer geld over."
                },
                {
                  icon: CheckCircle,
                  titel: "Alles Inclusief",
                  beschrijving: "Panelen, omvormer, montagesysteem, bekabeling én professionele montage. Geen verborgen kosten."
                },
                {
                  icon: Truck,
                  titel: "Thuisbezorging",
                  beschrijving: "Alle materialen worden netjes bij jou thuis bezorgd. Jij hoeft niets te regelen."
                },
                {
                  icon: Wrench,
                  titel: "Gecertificeerde Montage",
                  beschrijving: "Onze monteurs zijn gecertificeerd en hebben jaren ervaring met zonnepaneel installaties."
                },
                {
                  icon: Zap,
                  titel: "25 Jaar Garantie",
                  beschrijving: "Langdurige garantie op panelen en vakmanschap. Je investering is volledig beschermd."
                },
                {
                  icon: Package,
                  titel: "Complete Service",
                  beschrijving: "Van advies tot nazorg. Wij begeleiden je door het hele proces voor optimaal resultaat."
                }
              ].map((voordeel, index) => (
                <Card key={index} className="p-6 text-center">
                  <voordeel.icon className="h-12 w-12 text-primary mx-auto mb-4" />
                  <h3 className="text-xl font-semibold mb-3">{voordeel.titel}</h3>
                  <p className="text-muted-foreground">{voordeel.beschrijving}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Sol-Vi Garantie Section */}
        <section className="py-20 px-4 bg-gradient-to-br from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20">
          <div className="container mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Waarom Sol-Vi <span className="text-primary">Het Risico Neemt</span>
              </h2>
              <p className="text-xl text-muted-foreground">
                Wij geloven zo sterk in onze systemen dat we het risico volledig op ons nemen
              </p>
            </div>

            <div className="grid lg:grid-cols-3 gap-8">
              <Card className="p-6 border-green-200 dark:border-green-800">
                <CardHeader>
                  <CardTitle className="text-green-700 dark:text-green-300">🛡️ Prestatie Garantie</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Levert jouw systeem minder op dan berekend? Dan betaal je minder. Werkt het helemaal niet? Dan betaal je niets tot het probleem is opgelost.
                  </p>
                </CardContent>
              </Card>

              <Card className="p-6 border-blue-200 dark:border-blue-800">
                <CardHeader>
                  <CardTitle className="text-blue-700 dark:text-blue-300">📊 Transparante Monitoring</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Via onze app zie je real-time hoeveel jouw systeem opwekt en bespaart. Jouw maandelijkse Sol-Vi betaling is altijd gebaseerd op werkelijke besparing.
                  </p>
                </CardContent>
              </Card>

              <Card className="p-6 border-purple-200 dark:border-purple-800">
                <CardHeader>
                  <CardTitle className="text-purple-700 dark:text-purple-300">🔧 Gratis Onderhoud</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Gedurende de afbetaalperiode zorgen wij voor alle onderhoud en reparaties. Jouw systeem blijft optimaal presteren zonder extra kosten.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Community Features Section */}
        <section className="py-20 px-4 bg-muted/30">
          <div className="container mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
              De SOL VI Community - Samen Bouwen aan de Toekomst
            </h2>
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h3 className="text-2xl font-bold mb-6">SOL VI Laadpark Netwerk</h3>
                <p className="text-muted-foreground mb-6 leading-relaxed">
                  SOL VI zet zich voortdurend in om innovatief, vernieuwend en rendabeler te worden. 
                  Denk aan SOL VI's eigen laadparken voor auto's met stroom die van onze partners/klanten 
                  teruggeleverd of verkocht is aan SOL VI. Zo openen we nieuwe verdienmodellen waar iedereen van profiteert.
                </p>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold">Groene Energie Circulatie</h4>
                      <p className="text-sm text-muted-foreground">Overschot van partners wordt gebruikt voor EV-laden</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold">Community Rendement</h4>
                      <p className="text-sm text-muted-foreground">Investeert mee en profiteert van laadpark opbrengsten</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold">Innovatieve Verdienmodellen</h4>
                      <p className="text-sm text-muted-foreground">Nieuwe inkomstenbronnen voor alle betrokkenen</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="text-2xl font-bold mb-6">Goede Doelen Community</h3>
                <p className="text-muted-foreground mb-6 leading-relaxed">
                  We willen "goede doelen" opzetten in beheer van ons en de community. 
                  Denk aan kinderen met 1 ouder met een te laag inkomen om schoolreisjes, 
                  verjaardagscadeautjes of kerstcadeautjes te kopen - zij kunnen zich aanmelden voor steun.
                </p>
                <div className="bg-orange-50 dark:bg-orange-950 p-6 rounded-lg border border-orange-200 dark:border-orange-800">
                  <h4 className="font-semibold text-orange-800 dark:text-orange-200 mb-3">Hoe het werkt:</h4>
                  <div className="space-y-2 text-sm text-orange-700 dark:text-orange-300">
                    <p>• Gezinnen kunnen zich aanmelden via onze community</p>
                    <p>• 100% transparant: je ziet precies waar je bijdrage heen gaat</p>
                    <p>• Direct impact: schoolreisjes, verjaardagen, kerst mogelijk maken</p>
                    <p>• Community beheert samen met SOL VI alle aanvragen</p>
                  </div>
                </div>
                <div className="mt-6">
                  <Button className="w-full">Meer Info over Goede Doelen</Button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Extra Projecten Section */}
        <section className="py-20 px-4">
          <div className="container mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Meer Dan <span className="text-primary">Alleen Zonnepanelen</span>
              </h2>
              <p className="text-xl text-muted-foreground">
                Heb je nog andere projecten? Laat ze meefinancieren door je energiebesparing!
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                <h3 className="text-2xl font-bold">Financier Je Droomprojecten</h3>
                <p className="text-lg text-muted-foreground">
                  Door extra panelen toe te voegen aan je systeem, kun je ook andere projecten laten financieren door je energiebesparing. 
                  Sol-Vi bepaalt samen met jou het haalbare startbudget.
                </p>
                
                <div className="space-y-4">
                  {[
                    { project: "Nieuwe Vloer", budget: "€3.000 - €8.000", panelen: "4-8 extra panelen" },
                    { project: "Tuinhuis/Schuur", budget: "€5.000 - €15.000", panelen: "6-15 extra panelen" },
                    { project: "Badkamer Renovatie", budget: "€8.000 - €20.000", panelen: "10-25 extra panelen" },
                    { project: "Keuken Upgrade", budget: "€10.000 - €25.000", panelen: "13-30 extra panelen" }
                  ].map((item, index) => (
                    <div key={index} className="flex justify-between items-center p-4 bg-muted/50 rounded-lg">
                      <div>
                        <div className="font-medium">{item.project}</div>
                        <div className="text-sm text-muted-foreground">{item.budget}</div>
                      </div>
                      <div className="text-sm text-primary font-medium">{item.panelen}</div>
                    </div>
                  ))}
                </div>
              </div>

              <Card className="p-6 bg-primary/5 border-primary/20">
                <CardHeader>
                  <CardTitle>Zo Werkt Het</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-primary-foreground font-bold text-sm">1</div>
                    <div>
                      <h4 className="font-medium">Vertel Ons Je Plannen</h4>
                      <p className="text-sm text-muted-foreground">Wat wil je gaan doen? Renovatie, nieuwe tuin, auto?</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-primary-foreground font-bold text-sm">2</div>
                    <div>
                      <h4 className="font-medium">Wij Berekenen De Mogelijkheden</h4>
                      <p className="text-sm text-muted-foreground">Hoeveel extra panelen zijn nodig om jouw project te financieren?</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-primary-foreground font-bold text-sm">3</div>
                    <div>
                      <h4 className="font-medium">Startbudget Beschikbaar</h4>
                      <p className="text-sm text-muted-foreground">Na installatie krijg je het startbudget voor je project!</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 px-4 bg-muted/50">
          <div className="container mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Veelgestelde Vragen</h2>
              <p className="text-xl text-muted-foreground">
                Alles wat je wilt weten over het Sol-Vi partnership model
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-8">
              {[
                {
                  vraag: "Wat als mijn panelen minder opleveren dan verwacht?",
                  antwoord: "Dan betaal je minder! Jouw maandelijkse betaling is altijd gebaseerd op werkelijke besparing. Presteert het systeem slecht? Dan lossen wij het probleem op zonder kosten voor jou."
                },
                {
                  vraag: "Hoe wordt mijn besparing gemeten?",
                  antwoord: "Via slimme monitoring meten we continu hoeveel energie je systeem opwekt en hoeveel je bespaart. Deze data zie je real-time in onze app."
                },
                {
                  vraag: "Kan ik tussentijds afkopen?",
                  antwoord: "Ja, je kunt altijd vervroegd afkopen tegen de resterende systeemwaarde minus al betaalde Sol-Vi betalingen."
                },
                {
                  vraag: "Wat gebeurt er bij verhuizing?",
                  antwoord: "Het systeem blijft bij het huis. Je kunt de restschuld overdragen aan de nieuwe eigenaar of afkopen voor verkoop."
                },
                {
                  vraag: "Hoe bepalen jullie of extra projecten haalbaar zijn?",
                  antwoord: "We kijken naar je dakruimte, energiebesparing per extra paneel en de projectkosten. Alles moet verantwoord financierbaar zijn."
                },
                {
                  vraag: "Wat als het systeem kapot gaat?",
                  antwoord: "Gedurende de afbetaalperiode repareren wij alles gratis. Het systeem heeft 25 jaar garantie, wij zorgen dat het blijft presteren."
                }
              ].map((faq, index) => (
                <Card key={index} className="p-6">
                  <CardHeader>
                    <CardTitle className="text-lg">{faq.vraag}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">{faq.antwoord}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-4 bg-gradient-to-r from-primary to-primary/80">
          <div className="container mx-auto text-center text-primary-foreground">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Start Je Sol-Vi Partnership Vandaag
            </h2>
            <p className="text-xl mb-8 opacity-90">
              Risico-vrij besparen vanaf dag één. Betaal alleen wat je bespaart.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button size="lg" variant="secondary">
                Word SOL VI Partner <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button size="lg" variant="outline" className="bg-transparent border-white text-white hover:bg-white hover:text-primary">
                Plan Gratis Adviesgesprek
              </Button>
              <Button size="lg" variant="outline" className="bg-transparent border-white text-white hover:bg-white hover:text-primary">
                Laadpark Info
              </Button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      </div>
  );
};

const ZonnepaneelPakketten = () => {
  return (
    <ConfiguratorProvider>
      <ZonnepaneelPakkettenContent />
    </ConfiguratorProvider>
  );
};

export default ZonnepaneelPakketten;